import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCAL_CART_KEY = 'narkonline_guest_cart';

function getLocalCart() {
  try {
    const data = localStorage.getItem(LOCAL_CART_KEY);
    return data ? JSON.parse(data) : { items: [], total: 0, item_count: 0 };
  } catch {
    return { items: [], total: 0, item_count: 0 };
  }
}

function saveLocalCart(cart) {
  localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(cart));
}

function clearLocalCart() {
  localStorage.removeItem(LOCAL_CART_KEY);
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Sync guest cart to server on login
  const syncGuestCart = useCallback(async () => {
    const guestCart = getLocalCart();
    if (guestCart.items.length === 0) return;

    try {
      for (const item of guestCart.items) {
        await axios.post(`${API}/cart/add`, {
          product_id: item.product_id,
          quantity: item.quantity
        });
      }
      clearLocalCart();
    } catch (error) {
      console.error('Failed to sync guest cart:', error);
    }
  }, []);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart(getLocalCart());
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      syncGuestCart().then(() => fetchCart());
    } else {
      fetchCart();
    }
  }, [isAuthenticated, syncGuestCart, fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1, product = null) => {
    if (!isAuthenticated) {
      // Guest cart: store locally
      const localCart = getLocalCart();
      const existing = localCart.items.find(i => i.product_id === productId);

      if (existing) {
        existing.quantity += quantity;
        existing.subtotal = existing.price * existing.quantity;
      } else if (product) {
        localCart.items.push({
          product_id: productId,
          name: product.name,
          price: product.selling_price,
          quantity: quantity,
          subtotal: product.selling_price * quantity,
          image_url: product.image_url,
          in_stock: product.in_stock
        });
      }

      localCart.total = localCart.items.reduce((s, i) => s + i.subtotal, 0);
      localCart.item_count = localCart.items.reduce((s, i) => s + i.quantity, 0);
      saveLocalCart(localCart);
      setCart({ ...localCart });
      return true;
    }

    try {
      const response = await axios.post(`${API}/cart/add`, { product_id: productId, quantity });
      setCart(response.data);
      return true;
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  }, [isAuthenticated]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (!isAuthenticated) {
      const localCart = getLocalCart();
      const item = localCart.items.find(i => i.product_id === productId);
      if (item) {
        if (quantity <= 0) {
          localCart.items = localCart.items.filter(i => i.product_id !== productId);
        } else {
          item.quantity = quantity;
          item.subtotal = item.price * quantity;
        }
        localCart.total = localCart.items.reduce((s, i) => s + i.subtotal, 0);
        localCart.item_count = localCart.items.reduce((s, i) => s + i.quantity, 0);
        saveLocalCart(localCart);
        setCart({ ...localCart });
      }
      return;
    }

    try {
      const response = await axios.put(`${API}/cart/update/${productId}?quantity=${quantity}`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to update cart:', error);
      throw error;
    }
  }, [isAuthenticated]);

  const removeFromCart = useCallback(async (productId) => {
    if (!isAuthenticated) {
      const localCart = getLocalCart();
      localCart.items = localCart.items.filter(i => i.product_id !== productId);
      localCart.total = localCart.items.reduce((s, i) => s + i.subtotal, 0);
      localCart.item_count = localCart.items.reduce((s, i) => s + i.quantity, 0);
      saveLocalCart(localCart);
      setCart({ ...localCart });
      return;
    }

    try {
      const response = await axios.delete(`${API}/cart/remove/${productId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      throw error;
    }
  }, [isAuthenticated]);

  const clearCart = useCallback(async () => {
    if (!isAuthenticated) {
      clearLocalCart();
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }

    try {
      await axios.delete(`${API}/cart/clear`);
      setCart({ items: [], total: 0, item_count: 0 });
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  }, [isAuthenticated]);

  const value = useMemo(() => ({
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart
  }), [cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
